import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  targetDailyQuery,
  targetQuery,
  targetResultsQuery,
  targetStatsQuery,
  targetTimelineQuery,
} from '@/api/queries'
import type { StatsWindow } from '@/api/types'
import { DEFAULT_WINDOW, validateWindowSearch } from '@/lib/windowSearch'
import { ResponseTimeChart, StatCard, UptimeBars, WindowPicker } from '@/components/Charts'
import { StatusBadge } from '@/components/StatusBadge'
import { UptimeDays } from '@/components/UptimeDays'
import { Card, Loading } from '@/components/ui'
import { formatDateTime, formatMs, formatPercent, formatRelative, formatTime, intervalLabel } from '@/lib/format'

export const Route = createFileRoute('/_app/services/$serviceId/targets/$targetId')({
  validateSearch: validateWindowSearch,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(targetQuery(params.targetId)),
  component: TargetDetailPage,
})

function TargetDetailPage() {
  const { serviceId, targetId } = Route.useParams()
  const { window = DEFAULT_WINDOW } = Route.useSearch()
  const navigate = Route.useNavigate()

  // `replace` schvalne: prepinani oken nema zaplevelit historii prohlizece,
  // tlacitko zpet ma vratit na predchozi stranku, ne o jedno okno vedle.
  const setWindow = (next: StatsWindow) => navigate({ search: { window: next }, replace: true })

  const { data: target } = useQuery(targetQuery(targetId))
  const { data: stats } = useQuery(targetStatsQuery(targetId, window))
  const { data: timeline } = useQuery(targetTimelineQuery(targetId, window))
  const { data: results } = useQuery(targetResultsQuery(targetId))
  const { data: daily } = useQuery(targetDailyQuery(targetId))

  if (!target) return <Loading />

  return (
    <div className="space-y-6">
      <div>
        <Link to="/services/$serviceId" params={{ serviceId }} className="text-sm text-slate-500 hover:text-brand-700">
          ← Zpet na sluzbu
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900">{target.name}</h1>
          <StatusBadge status={target.currentStatus} />
          {target.isPublic && (
            <Link
              to="/public/target/$targetId"
              params={{ targetId }}
              className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              verejny odkaz
            </Link>
          )}
        </div>
        <a
          href={target.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-sm text-slate-500 hover:text-brand-700"
        >
          {target.method} {target.url}
        </a>
        <p className="text-sm text-slate-400">
          Kazdych {intervalLabel(target.intervalSeconds)} · ocekavany kod {target.expectedStatusCode} · posledni
          mereni {formatRelative(target.lastCheckedAt)}
        </p>
        {target.currentStatus === 'DOWN' && target.lastErrorMessage && (
          <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{target.lastErrorMessage}</p>
        )}
      </div>

      {/* Denni pruh za 90 dnu nezavisi na zvolenem okne — dava dlouhy kontext,
          ktery se do oken 1h az 30d nevejde. Dny bez mereni jsou sede. */}
      <Card>
        <h3 className="mb-3 text-sm font-medium text-slate-700">Poslednich 90 dnu</h3>
        <UptimeDays days={daily ?? []} />
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-700">Historie</h2>
        <WindowPicker value={window} onChange={setWindow} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={`SLA ${window}`}
          value={formatPercent(stats?.uptimePercent)}
          tone={stats?.uptimePercent === 100 ? 'good' : (stats?.uptimePercent ?? 100) < 99 ? 'bad' : 'warn'}
          hint={`${stats?.upSamples ?? 0} z ${stats?.samples ?? 0} mereni`}
        />
        <StatCard label="Prumerna odezva" value={formatMs(stats?.avgResponseMs)} />
        {/* p95 rika, jak je na tom pomalejsi desetina pozadavku — prumer sam
            o sobe vypadku odezvy nevsimne. */}
        <StatCard label="p95 odezva" value={formatMs(stats?.p95ResponseMs)} />
        <StatCard label="Nejpomalejsi" value={formatMs(stats?.maxResponseMs)} />
      </div>

      <Card>
        <h3 className="mb-3 text-sm font-medium text-slate-700">Dostupnost</h3>
        <UptimeBars data={timeline ?? []} />
      </Card>

      <Card>
        <h3 className="mb-3 text-sm font-medium text-slate-700">Doba odezvy</h3>
        <ResponseTimeChart data={timeline ?? []} window={window} />
      </Card>

      <Card className="overflow-x-auto p-0">
        <h3 className="px-4 pt-4 text-sm font-medium text-slate-700">Posledni mereni</h3>
        <table className="mt-3 w-full min-w-[560px] text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Cas</th>
              <th className="px-4 py-2">Stav</th>
              <th className="px-4 py-2">Kod</th>
              <th className="px-4 py-2 text-right">Odezva</th>
              <th className="px-4 py-2">Chyba</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(results ?? []).map((result) => (
              <tr key={result.id ?? result.checkedAt} className={result.isUp ? '' : 'bg-red-50/50'}>
                <td className="px-4 py-2 text-slate-500" title={formatDateTime(result.checkedAt)}>
                  {formatTime(result.checkedAt)}
                </td>
                <td className="px-4 py-2">
                  <span className={result.isUp ? 'text-green-600' : 'text-red-600'}>
                    {result.isUp ? 'OK' : 'Chyba'}
                  </span>
                </td>
                <td className="px-4 py-2 tabular-nums text-slate-600">{result.statusCode ?? '—'}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatMs(result.responseTimeMs)}</td>
                <td className="max-w-[280px] truncate px-4 py-2 text-slate-400">{result.errorMessage ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(results ?? []).length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-slate-400">Zatim zadna mereni</p>
        )}
      </Card>
    </div>
  )
}
